import { AppRoutes } from "@/routes/config";
import { Outlet, RouteObject } from "react-router-dom";
import PageNotFound from "@/pages/PageNotFound";
import { LoginLayout } from "@/components/layout/LoginLayout/LoginLayout";
import AuthCallbackCognito from "@/pages/auth/AuthCallbackCognito";
import AuthCallback from "@/pages/auth/AuthCallback";
import ConfirmEmail from "@/pages/auth/ConfirmEmail";
import Login from "@/pages/auth/Login";
import LoginCredentials from "@/pages/auth/LoginCredentials";
import LoginVerify from "@/pages/auth/LoginVerify";
import Logout from "@/pages/auth/Logout";
import Recover from "@/pages/auth/Recover";
import RecoverComplete from "@/pages/auth/RecoverComplete";
import Register from "@/pages/auth/Register";
import RegisterComplete from "@/pages/auth/RegisterComplete";
import ResetPassword from "@/pages/auth/ResetPassword";
import ResetPasswordComplete from "@/pages/auth/ResetPasswordComplete";
import ServerErrorPage from "@/pages/ServerErrorPage";
import { Maintenance } from "@/features/maintenance/pages/Maintenance";
import { MaintenanceGuard } from "@/features/maintenance/components/MaintenanceGuard";
import { CenterLayoutWithAuth } from "@/components/layout/CenterLayout/CenterLayout";
import { AppRouteNames } from "@/routes/constants";
import Dashboard from "@/pages/dashboard/Dashboard";
import MintOauthRedirect from "@/pages/mintOauthRedirect/MintOauthRedirect";
import ExternalAuthBridge from "@/features/auth/components/ExternalAuthBridge";
import CustomerUserList from "@/pages/customer-user/CustomerUserList";
import CustomerUserDetail from "@/pages/customer-user/CustomerUserDetail";
import CustomerUserCreate from "@/pages/customer-user/CustomerUserCreate";
import CustomerUserEdit from "@/pages/customer-user/CustomerUserEdit";
import UserSettingChangeEmail from "@/pages/user-setting/UserSettingChangeEmail";
import UserSettingDetail from "@/pages/user-setting/UserSettingDetail";
import UserSettingEdit from "@/pages/user-setting/UserSettingEdit";
import { RequireRole } from "@/features/auth/components/RequireRole";
import { USER_ROLE } from "@/types/enum";

const appRoutes: RouteObject[] = [
  {
    element: <LoginLayout />,
    children: [
      {
        path: AppRoutes.resetPassword,
        element: <ResetPassword />,
      },
      {
        path: AppRoutes.register,
        element: <Register />,
      },
      {
        path: AppRoutes.loginCredentials,
        element: <LoginCredentials />,
      },
      {
        path: AppRoutes.loginVerify,
        element: <LoginVerify />,
      },
      {
        path: AppRoutes.confirmEmail,
        element: <ConfirmEmail />,
      },
      {
        path: AppRoutes.authCallback,
        element: <AuthCallback />,
      },
      {
        path: AppRoutes.login,
        element: <Login />,
      },
      {
        path: AppRoutes.recover,
        element: <Recover />,
      },
      {
        path: AppRoutes.recoverComplete,
        element: <RecoverComplete />,
      },
      {
        path: AppRoutes.resetPasswordComplete,
        element: <ResetPasswordComplete />,
      },
      {
        path: AppRoutes.registerComplete,
        element: <RegisterComplete />,
      },
    ],
  },
  {
    path: AppRoutes.mintOauthRedirect,
    element: <MintOauthRedirect />,
  },
  {
    element: <CenterLayoutWithAuth />,
    children: [
      {
        path: AppRoutes.home,
        element: <Dashboard />,
        id: AppRouteNames.home.value,
      },
      {
        path: AppRoutes.customerUsers,
        // List + detail: 管理者 + 一般 (viewer → 404). Mutations gated separately below.
        element: (
          <RequireRole allow={[USER_ROLE.ADMIN, USER_ROLE.MEMBER]}>
            <CustomerUserList />
          </RequireRole>
        ),
        id: AppRouteNames.customerUsers.value,
      },
      {
        path: AppRoutes.customerUserCreate,
        element: (
          <RequireRole allow={[USER_ROLE.ADMIN]}>
            <CustomerUserCreate />
          </RequireRole>
        ),
        id: AppRouteNames.customerUserCreate.value,
      },
      {
        path: AppRoutes.customerUserEdit,
        // Edit is 管理者-only.
        element: (
          <RequireRole allow={[USER_ROLE.ADMIN]}>
            <CustomerUserEdit />
          </RequireRole>
        ),
        id: AppRouteNames.customerUserEdit.value,
      },
      {
        path: AppRoutes.customerUserDetail,
        element: (
          <RequireRole allow={[USER_ROLE.ADMIN, USER_ROLE.MEMBER]}>
            <CustomerUserDetail />
          </RequireRole>
        ),
        id: AppRouteNames.customerUserDetail.value,
      },
      {
        // Own account settings: available to every authenticated role.
        path: AppRoutes.userSettingDetail,
        element: <UserSettingDetail />,
        id: AppRouteNames.userSettingDetail.value,
      },
      {
        path: AppRoutes.userSettingEdit,
        element: <UserSettingEdit />,
        id: AppRouteNames.userSettingEdit.value,
      },
      {
        path: AppRoutes.userSettingChangeEmail,
        element: <UserSettingChangeEmail />,
        id: AppRouteNames.userSettingChangeEmail.value,
      },
    ],
  },
  {
    path: AppRoutes.logout,
    element: <Logout />,
  },
  { path: AppRoutes.externalAuthBridge, element: <ExternalAuthBridge /> },
  { path: AppRoutes.authCallbackCognito, element: <AuthCallbackCognito /> },
  { path: AppRoutes.serverErrorPage, element: <ServerErrorPage /> },
  // Full-screen maintenance page (outside the app layout, so no header/menu).
  { path: AppRoutes.maintenance, element: <Maintenance />, id: AppRouteNames.maintenance.value },
  // Full-screen 404 (outside the app layout, so no header/menu). RequireRole redirects here.
  { path: AppRoutes.pageNotFound, element: <PageNotFound /> },
  { path: "*", element: <PageNotFound /> },
];

// Pathless root route: re-evaluates the maintenance redirect on every navigation,
// so client-side transitions are gated the same way as a hard page load.
export const routes: RouteObject[] = [
  {
    element: (
      <MaintenanceGuard>
        <Outlet />
      </MaintenanceGuard>
    ),
    children: appRoutes,
  },
];

const flattenRoutes = (routes: RouteObject[]): RouteObject[] => {
  const flatRoutes: RouteObject[] = [];

  const extractChildren = (route: RouteObject) => {
    if (route.children) {
      route.children.forEach((child) => {
        extractChildren(child);
      });
    } else {
      flatRoutes.push(route);
    }
  };

  routes.forEach((route) => extractChildren(route));

  return flatRoutes;
};

export const flattenedRoutes = flattenRoutes(routes);
